<template>
  <input
    class="preg__input"
    type="text"
    inputmode="numeric"
    autocomplete="off"
    :value="shown"
    :disabled="disabled"
    @pointerdown="onPointerDown"
    @focus="onFocus"
    @input="onInput"
    @blur="onBlur"
    @keydown="onKeydown"
  />
</template>

<script setup lang="ts">
import { computed, nextTick, ref } from 'vue'

const props = withDefaults(
  defineProps<{
    modelValue?: number | null
    disabled?: boolean
  }>(),
  { modelValue: 0, disabled: false },
)

const emit = defineEmits<{
  'update:modelValue': [value: number]
}>()

const editing = ref(false)
const draft = ref('')
let fromPointer = false

function digitsOf(value: unknown): string {
  return String(value ?? '')
    .replace(/[^\d]/g, '')
    .replace(/^0+(?=\d)/, '')
    .slice(0, 12)
}

function grouped(value: number): string {
  return Math.max(0, Math.trunc(Number(value) || 0)).toLocaleString('ko-KR')
}

const shown = computed(() => (editing.value ? draft.value : grouped(props.modelValue || 0)))

function commit(raw: string) {
  const clean = digitsOf(raw)
  draft.value = clean
  emit('update:modelValue', clean === '' ? 0 : Number(clean))
  return clean
}

/** 쉼표가 있는 글자 위에서 누른 위치를, 숫자만 남긴 뒤의 커서 자리로 바꾼다. */
function digitIndexAt(input: HTMLInputElement, clientX: number): number {
  const style = getComputedStyle(input)
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  if (!ctx) return digitsOf(input.value).length
  ctx.font = style.font
  const pad = parseFloat(style.paddingLeft) || 0
  const x = clientX - input.getBoundingClientRect().left - pad + input.scrollLeft
  const text = input.value
  let used = 0
  let count = 0
  for (let i = 0; i < text.length; i += 1) {
    const width = ctx.measureText(text[i]).width
    if (used + width / 2 > x) break
    used += width
    if (/\d/.test(text[i])) count += 1
  }
  return count
}

function placeCaret(input: HTMLInputElement, caret: number | 'all') {
  nextTick(() => {
    if (caret === 'all') {
      input.select()
      return
    }
    const pos = Math.min(Math.max(0, caret), draft.value.length)
    input.setSelectionRange(pos, pos)
  })
}

function beginEdit(input: HTMLInputElement, caret: number | 'all') {
  editing.value = true
  draft.value = digitsOf(props.modelValue || 0)
  placeCaret(input, caret)
}

function onPointerDown(event: PointerEvent) {
  if (props.disabled) return
  const input = event.currentTarget as HTMLInputElement
  if (document.activeElement === input) return
  fromPointer = true
  event.preventDefault()
  const index = digitIndexAt(input, event.clientX)
  input.focus()
  beginEdit(input, index)
}

function onFocus(event: FocusEvent) {
  if (props.disabled || editing.value) return
  const input = event.currentTarget as HTMLInputElement
  const pointer = fromPointer
  fromPointer = false
  if (pointer) return
  beginEdit(input, 'all')
}

function onInput(event: Event) {
  const input = event.target as HTMLInputElement
  const before = input.selectionStart ?? input.value.length
  const digitsBefore = input.value.slice(0, before).replace(/[^\d]/g, '').length
  const clean = commit(input.value)
  if (input.value !== clean) {
    input.value = clean
    const pos = Math.min(digitsBefore, clean.length)
    input.setSelectionRange(pos, pos)
  }
}

function onBlur() {
  editing.value = false
  if (!digitsOf(draft.value)) emit('update:modelValue', 0)
}

function onKeydown(event: KeyboardEvent) {
  if (event.ctrlKey || event.metaKey || event.altKey) return
  if (['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return
  if (/^\d$/.test(event.key)) return
  event.preventDefault()
}
</script>
